import { atom } from 'jotai';
import { isEqual } from 'lodash-es';
import type { FolderId, ViewModelId, WorkspaceId, ViewId } from 'balus-common';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { ViewEntity } from '@view-model/domain/view';
import type { Workspace } from '@workspace/domain/workspace';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';

export const workspacesAtom = atom<Record<WorkspaceId, Workspace>>({});
export const folderTreesAtom = atom<Record<WorkspaceId, RootFolderTree>>({});
export const foldersAtom = atom<Record<FolderId, Folder>>({});
export const viewModelsAtom = atom<Record<ViewModelId, ViewModelEntity>>({});
export const viewsAtom = atom<Record<ViewModelId, Record<ViewId, ViewEntity>>>({});

export const firstLoadAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      workspaces: Record<WorkspaceId, Workspace>;
      folderTrees: Record<WorkspaceId, RootFolderTree>;
      folders: Record<FolderId, Folder>;
      viewModels: Record<ViewModelId, ViewModelEntity>;
    }
  ) => {
    set(workspacesAtom, payload.workspaces);
    set(folderTreesAtom, payload.folderTrees);
    set(foldersAtom, payload.folders);
    set(viewModelsAtom, payload.viewModels);
  }
);

export const addWorkspaceAtom = atom(null, (get, set, payload: { workspace: Workspace }) => {
  const currentWorkspaces = get(workspacesAtom);
  if (currentWorkspaces[payload.workspace.id] && currentWorkspaces[payload.workspace.id].isEqual(payload.workspace)) {
    return;
  }
  set(workspacesAtom, {
    ...currentWorkspaces,
    [payload.workspace.id]: payload.workspace,
  });
});

export const addRootFolderTreeAtom = atom(
  null,
  (get, set, payload: { workspaceId: WorkspaceId; rootFolderTree: RootFolderTree }) => {
    set(folderTreesAtom, {
      ...get(folderTreesAtom),
      [payload.workspaceId]: payload.rootFolderTree,
    });
  }
);

export const setFolderAtom = atom(null, (get, set, payload: { folder: Folder }) => {
  const currentFolders = get(foldersAtom);
  if (currentFolders[payload.folder.id] && isEqual(currentFolders[payload.folder.id].dump(), payload.folder.dump())) {
    return;
  }
  set(foldersAtom, {
    ...currentFolders,
    [payload.folder.id]: payload.folder,
  });
});

export const removeFolderAtom = atom(null, (get, set, payload: { folderId: FolderId }) => {
  const { [payload.folderId]: removedFolder, ...newFolders } = get(foldersAtom);
  set(foldersAtom, newFolders);
});

export const setViewModelAtom = atom(null, (get, set, payload: { viewModel: ViewModelEntity }) => {
  const currentViewModels = get(viewModelsAtom);
  if (
    currentViewModels[payload.viewModel.id] &&
    currentViewModels[payload.viewModel.id].isEqualExcludingUpdatedAt(payload.viewModel)
  ) {
    return;
  }
  set(viewModelsAtom, {
    ...currentViewModels,
    [payload.viewModel.id]: payload.viewModel,
  });
});

export const removeViewModelAtom = atom(null, (get, set, payload: { viewModelId: ViewModelId }) => {
  const { [payload.viewModelId]: removedViewModel, ...newViewModels } = get(viewModelsAtom);
  set(viewModelsAtom, newViewModels);

  // ViewModelが削除された時、それに紐づくViewも削除する
  const currentViews = get(viewsAtom);
  const { [payload.viewModelId]: removedViews, ...newViews } = currentViews;
  set(viewsAtom, newViews);
});

export const setViewAtom = atom(null, (get, set, payload: { view: ViewEntity; viewModelId: ViewModelId }) => {
  const currentViews = get(viewsAtom);
  const currentViewModelViews = currentViews[payload.viewModelId] || {};

  if (currentViewModelViews[payload.view.id] && currentViewModelViews[payload.view.id].isEqual(payload.view)) {
    return;
  }

  set(viewsAtom, {
    ...currentViews,
    [payload.viewModelId]: {
      ...currentViewModelViews,
      [payload.view.id]: payload.view,
    },
  });
});

export const removeViewAtom = atom(null, (get, set, payload: { viewId: ViewId; viewModelId: ViewModelId }) => {
  const currentViews = get(viewsAtom);
  const currentViewModelViews = currentViews[payload.viewModelId] || {};
  const { [payload.viewId]: removedView, ...newViewModelViews } = currentViewModelViews;

  set(viewsAtom, {
    ...currentViews,
    [payload.viewModelId]: newViewModelViews,
  });
});

export const removeBulkAtom = atom(null, (get, set, payload: { workspaceId: WorkspaceId }) => {
  const { workspaceId } = payload;
  const currentWorkspaces = get(workspacesAtom);
  const currentFolderTrees = get(folderTreesAtom);
  const currentFolders = get(foldersAtom);
  const currentViewModels = get(viewModelsAtom);
  const currentViews = get(viewsAtom);

  const { [workspaceId]: targetWorkspace, ...newWorkspaces } = currentWorkspaces;
  const { [workspaceId]: targetFolderTree, ...newFolderTrees } = currentFolderTrees;

  if (!targetFolderTree) return;

  const targetFolderIds = targetFolderTree.getAllFolderIds();

  const targetViewModelIds = targetFolderTree.getAllViewModelIds();

  set(workspacesAtom, newWorkspaces);
  set(folderTreesAtom, newFolderTrees);
  set(
    foldersAtom,
    Object.entries(currentFolders).reduce((res, [folderId, folder]) => {
      if (targetFolderIds.includes(folderId)) {
        return res;
      } else {
        return {
          ...res,
          [folderId]: folder,
        };
      }
    }, {})
  );
  set(
    viewModelsAtom,
    Object.entries(currentViewModels).reduce((res, [viewModelId, viewModel]) => {
      if (targetViewModelIds.includes(viewModelId)) {
        return res;
      } else {
        return {
          ...res,
          [viewModelId]: viewModel,
        };
      }
    }, {})
  );
  set(
    viewsAtom,
    Object.entries(currentViews).reduce((res, [viewModelId, views]) => {
      if (targetViewModelIds.includes(viewModelId)) {
        return res;
      } else {
        return {
          ...res,
          [viewModelId]: views,
        };
      }
    }, {})
  );
});
